import { InMemoryWebStorage, UserManager, WebStorageStateStore, type User } from "oidc-client-ts";
import type { CognitoConfig } from "../config";
import {
  safeReturnTo,
  type AuthSession,
  type AuthUser,
  type IdentityProvider,
  type LoginOptions,
} from "./session";

// =============================================================================
// Cognito-Anmeldung (Authorization Code + PKCE) – Entscheidung 39, Variante A
// =============================================================================
// oidc-client-ts übernimmt den OIDC-Ablauf (Entscheidung 36): Umleitung zu
// Cognito mit PKCE, Code-Tausch am Token-Endpunkt, Erneuerung per Refresh Token.
//
// Ablageorte:
//   Tokens (userStore)           -> InMemoryWebStorage: nur im JS-Speicher, weg
//                                   nach Neuladen/Tab-Schließen (Variante A)
//   Login-Zwischenstand          -> sessionStorage: muss die Umleitung zu Cognito
//   (stateStore: state, PKCE)       überleben; wird beim Rücksprung gelöscht,
//                                   enthält kein Token
//   zuletzt genutzter Anbieter   -> localStorage: nur der Name ("Google")

const LAST_PROVIDER_KEY = "ortura.lastProvider";

// Der Login-Zwischenstand, den wir über die Umleitung mitgeben.
interface LoginState {
  returnTo: string;
}

function readLastProvider(): IdentityProvider | null {
  try {
    const value = window.localStorage.getItem(LAST_PROVIDER_KEY);
    return value === "Google" || value === "Microsoft" ? value : null;
  } catch {
    // localStorage kann gesperrt sein (privates Fenster, Richtlinien) – dann eben ohne.
    return null;
  }
}

function writeLastProvider(provider: IdentityProvider | null): void {
  try {
    if (provider) window.localStorage.setItem(LAST_PROVIDER_KEY, provider);
    else window.localStorage.removeItem(LAST_PROVIDER_KEY);
  } catch {
    // siehe oben
  }
}

function toAuthUser(user: User): AuthUser {
  return {
    userId: user.profile.sub,
    email: typeof user.profile.email === "string" ? user.profile.email : undefined,
  };
}

export function createCognitoSession(config: CognitoConfig): AuthSession {
  const origin = window.location.origin;

  const manager = new UserManager({
    // Cognito veröffentlicht unter dieser Adresse sein OIDC-Discovery-Dokument
    // (Endpunkte für Login und Token, Signaturschlüssel).
    authority: `https://cognito-idp.${config.region}.amazonaws.com/${config.userPoolId}`,
    client_id: config.clientId,
    redirect_uri: `${origin}/callback`,
    response_type: "code",
    scope: "openid email profile",
    userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
    // Erneuert das Access Token kurz vor Ablauf still mit dem Refresh Token.
    automaticSilentRenew: true,
    // Alle nötigen Angaben stehen im ID Token, kein Extra-Aufruf.
    loadUserInfo: false,
  });

  return {
    kind: "cognito",

    async restore() {
      // Nach einem Neuladen ist der Speicher leer -> null (Variante A).
      const user = await manager.getUser();
      return user && !user.expired ? toAuthUser(user) : null;
    },

    async login({ provider, returnTo }: LoginOptions) {
      writeLastProvider(provider ?? null);
      const state: LoginState = { returnTo };
      await manager.signinRedirect({
        state,
        // identity_provider schickt Cognito direkt zum Anbieter, ohne eigene
        // Auswahlseite (Entscheidung 37).
        ...(provider && { extraQueryParams: { identity_provider: provider } }),
      });
      // Der Browser verlässt jetzt die Seite. Ein Promise, das nie endet,
      // verhindert, dass die App bis dahin weiterläuft.
      return new Promise<never>(() => {});
    },

    async completeLogin(url: string) {
      let user: User;
      try {
        user = await manager.signinRedirectCallback(url);
      } catch (error) {
        // Fehlgeschlagener Login: nicht beim nächsten Aufruf automatisch
        // erneut zum selben Anbieter springen (sonst Schleife).
        writeLastProvider(null);
        throw error;
      }
      const state = user.state as Partial<LoginState> | undefined;
      return { user: toAuthUser(user), returnTo: safeReturnTo(state?.returnTo) ?? "/select" };
    },

    async getAccessToken() {
      const user = await manager.getUser();
      if (!user) return null;
      if (!user.expired) return user.access_token;
      try {
        // signinSilent nutzt das Refresh Token (kein iframe, keine Umleitung).
        const renewed = await manager.signinSilent();
        return renewed?.access_token ?? null;
      } catch {
        // Refresh Token abgelaufen oder widerrufen -> abgemeldet.
        await manager.removeUser();
        return null;
      }
    },

    async logout() {
      await manager.removeUser();
      // Nach einer bewussten Abmeldung nicht automatisch wieder anmelden.
      writeLastProvider(null);
      // Cognito nennt im Discovery-Dokument keinen Logout-Endpunkt, deshalb
      // direkt: beendet die Cognito-Sitzung und springt zur Startseite zurück.
      // Die Rücksprung-Adresse muss im App-Client als Logout-URL eingetragen sein.
      const params = new URLSearchParams({ client_id: config.clientId, logout_uri: `${origin}/` });
      window.location.assign(`${config.domain}/logout?${params.toString()}`);
      // Wie bei login(): der Browser verlässt die Seite, nichts soll danach laufen.
      return new Promise<never>(() => {});
    },

    lastProvider: readLastProvider,
  };
}
